import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import WorkflowBuilder from "../components/WorkflowBuilder";
import WorkflowRuns, { runChipTone } from "../components/WorkflowRuns";
import { archiveWorkflow, fetchWorkflow, fetchWorkflows, runWorkflow } from "../lib/api";
import { useCan } from "../lib/session";
import type {
  ChipTone,
  WorkflowLastRun,
  WorkflowSummary,
  WorkflowTriggerSummary,
} from "../lib/types";

const LIBRARY_PREFIX = "library:";

interface WorkflowSeries {
  key: string;
  displayName: string;
  isLibrary: boolean;
  published: WorkflowSummary | null;
  draft: WorkflowSummary | null;
}

function newest(current: WorkflowSummary | null, candidate: WorkflowSummary): WorkflowSummary {
  return current === null || candidate.version > current.version ? candidate : current;
}

export function groupWorkflows(workflows: WorkflowSummary[]): WorkflowSeries[] {
  const groups = new Map<string, WorkflowSeries>();
  for (const workflow of workflows) {
    const isLibrary = workflow.name.startsWith(LIBRARY_PREFIX);
    const displayName = isLibrary
      ? workflow.name.slice(LIBRARY_PREFIX.length).trim() || workflow.name
      : workflow.name;
    const group = groups.get(workflow.name) ?? {
      key: workflow.name,
      displayName,
      isLibrary,
      published: null,
      draft: null,
    };
    if (workflow.status === "published") group.published = newest(group.published, workflow);
    if (workflow.status === "draft") group.draft = newest(group.draft, workflow);
    groups.set(workflow.name, group);
  }
  return [...groups.values()]
    .filter((group) => group.published !== null || group.draft !== null)
    .sort(
      (left, right) =>
        Number(right.isLibrary) - Number(left.isLibrary) ||
        left.displayName.localeCompare(right.displayName),
    );
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function scheduleDescription(trigger: WorkflowTriggerSummary): string {
  const cron = trigger.cron?.trim();
  const timezone = trigger.timezone || "UTC";
  const fields = cron?.split(/\s+/) ?? [];
  if (fields.length === 5) {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
    const minuteNumber = Number(minute);
    const hourNumber = Number(hour);
    if (
      Number.isInteger(minuteNumber) &&
      minuteNumber >= 0 &&
      minuteNumber < 60 &&
      Number.isInteger(hourNumber) &&
      hourNumber >= 0 &&
      hourNumber < 24 &&
      dayOfMonth === "*" &&
      month === "*"
    ) {
      const time = `${String(hourNumber).padStart(2, "0")}:${String(minuteNumber).padStart(2, "0")}`;
      if (dayOfWeek === "*") return `Daily at ${time} ${timezone}`;
      const weekday = Number(dayOfWeek);
      if (Number.isInteger(weekday) && weekday >= 0 && weekday <= 6) {
        return `${WEEKDAYS[weekday]}s at ${time} ${timezone}`;
      }
    }
  }
  return cron ? `Scheduled by rule · ${timezone}` : "Scheduled time unavailable";
}

function filterTickers(filter: Record<string, unknown> | null | undefined): string[] {
  const ticker = filter?.ticker;
  if (typeof ticker === "string" && ticker.trim()) return [ticker.trim().toUpperCase()];
  if (Array.isArray(ticker)) {
    return ticker.map(String).map((value) => value.trim().toUpperCase()).filter(Boolean);
  }
  return [];
}

export function triggerDescription(trigger: WorkflowTriggerSummary | null): string {
  if (!trigger) return "No trigger configured";
  if (trigger.type === "manual") return "On demand";
  if (trigger.type === "schedule") return scheduleDescription(trigger);
  if (trigger.type === "event") {
    const eventLabels: Record<string, string> = {
      coverage_gap_opened: "When a coverage gap opens",
      document_ingested: "When a new document arrives",
      facts_refreshed: "When company facts refresh",
      monitor_tripped: "When a monitor trips",
      observation_persisted: "When a new observation is recorded",
    };
    const label = eventLabels[trigger.event_type ?? ""] ?? "When its event occurs";
    const tickers = filterTickers(trigger.filter);
    return tickers.length > 0 ? `${label} · ${tickers.join(", ")}` : label;
  }
  return "Configured trigger";
}

function runtimeStatus(series: WorkflowSeries): { label: string; tone: ChipTone } {
  const published = series.published;
  if (!published) return { label: "Draft", tone: "amber" };
  if (!published.trigger) return { label: "Setup issue", tone: "red" };
  if (published.trigger.type === "manual") return { label: "Manual", tone: "blue" };
  if (published.trigger.enabled === true) return { label: "Armed", tone: "green" };
  if (published.trigger.enabled === false) return { label: "Off", tone: "gray" };
  return { label: "Not projected", tone: "amber" };
}

function runLabel(status: string): string {
  if (status === "waiting_on_approval") return "Needs approval";
  return status.replaceAll("_", " ");
}

function RunSnapshot({ label, run }: { label: string; run: WorkflowLastRun | null | undefined }) {
  return (
    <div className="agents-run-snapshot">
      <span>{label}</span>
      {run ? (
        <div>
          <StatusBadge tone={runChipTone(run.status)}>{runLabel(run.status)}</StatusBadge>
          <time dateTime={run.created_at}>{formatWhen(run.created_at)}</time>
        </div>
      ) : (
        <strong>Never</strong>
      )}
    </div>
  );
}

function AutomationCard({
  series,
  onEdit,
  onRan,
}: {
  series: WorkflowSeries;
  onEdit: (workflowId: string) => void;
  onRan: (runId: string) => void;
}) {
  const canWrite = useCan("workflows.write");
  const canRun = useCan("workflows.run");
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState<"live" | "test" | "archive" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const confirmationDescriptionId = useId();
  const confirmationActionRef = useRef<HTMLButtonElement>(null);
  const liveTriggerRef = useRef<HTMLButtonElement>(null);
  const testTriggerRef = useRef<HTMLButtonElement>(null);
  const archiveTriggerRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<{ current: HTMLButtonElement | null } | null>(null);
  const actionPendingRef = useRef(false);
  const published = series.published;
  const draft = series.draft;
  const primary = published ?? draft;
  const testDefinition = draft?.test_run_supported ? draft : published?.test_run_supported ? published : null;
  // Prefer the published definition: archiving it is the control that stops
  // future automatic work. A draft-only series can still be removed here.
  const archiveDefinition = published ?? draft;
  const state = runtimeStatus(series);

  const run = useMutation({
    mutationFn: ({ workflow, mode }: { workflow: WorkflowSummary; mode: "live" | "dry_run" }) =>
      runWorkflow(workflow.workflow_id, mode),
    onSuccess: (created) => {
      setConfirmation(null);
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: ["workflow-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["workflows"] });
      onRan(created.workflow_run_id);
    },
    onError: () => {
      setConfirmation(null);
      setProblem("The run could not be started. Try again or check Operations for service health.");
    },
  });
  const archive = useMutation({
    mutationFn: (workflow: WorkflowSummary) => archiveWorkflow(workflow.workflow_id),
    onSuccess: () => {
      setConfirmation(null);
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: () => {
      setConfirmation(null);
      setProblem("This definition could not be archived. Try again.");
    },
  });
  actionPendingRef.current = run.isPending || archive.isPending;

  useEffect(() => {
    if (!confirmation) return;
    const returnTarget = returnFocusRef.current;
    confirmationActionRef.current?.focus();
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || actionPendingRef.current) return;
      event.preventDefault();
      setConfirmation(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.setTimeout(() => returnTarget?.current?.focus(), 0);
    };
  }, [confirmation]);

  function requestConfirmation(
    next: "live" | "test" | "archive",
    returnTarget: { current: HTMLButtonElement | null },
  ) {
    returnFocusRef.current = returnTarget;
    setConfirmation(next);
  }

  if (!primary) return null;
  const liveRun = published?.last_live_run ?? null;
  const testRun = (draft?.last_test_run ?? published?.last_test_run) ?? null;

  return (
    <article className="agents-automation">
      <div className="agents-automation-head">
        <div>
          <span className="agents-automation-kind">
            {series.isLibrary ? "Built-in automation" : "Custom automation"}
          </span>
          <h3>{series.displayName}</h3>
        </div>
        <div className="agents-automation-badges">
          <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
          {published && draft ? <StatusBadge tone="amber">Draft update</StatusBadge> : null}
        </div>
      </div>

      <p className="agents-automation-description">
        {primary.description?.trim() || "No purpose has been documented for this automation."}
      </p>

      <dl className="agents-automation-facts">
        <div>
          <dt>Trigger</dt>
          <dd>{triggerDescription(published?.trigger ?? draft?.trigger ?? null)}</dd>
        </div>
        {published?.trigger?.enabled === true && published.trigger.type === "schedule" ? (
          <div>
            <dt>Next run</dt>
            <dd>
              {published.trigger.next_fire_at ? (
                <time dateTime={published.trigger.next_fire_at}>
                  {formatWhen(published.trigger.next_fire_at)}
                </time>
              ) : (
                "Waiting for scheduler"
              )}
            </dd>
          </div>
        ) : null}
      </dl>

      <div className="agents-run-history" aria-label={`Run history for ${series.displayName}`}>
        <RunSnapshot label="Last live run" run={liveRun} />
        <RunSnapshot label="Last test" run={testRun} />
      </div>

      {published?.trigger && published.trigger.type !== "manual" ? (
        <p className="agents-automation-note">
          {published.trigger.enabled === false
            ? "The automatic trigger is currently off."
            : "This automation starts from its configured trigger."} Event replay is not available
          from this page.
        </p>
      ) : null}

      {problem ? <div className="agents-card-problem" role="alert">{problem}</div> : null}

      {confirmation ? (
        <div
          className="agents-confirm"
          role="alertdialog"
          aria-label={`Confirm action for ${series.displayName}`}
          aria-describedby={confirmationDescriptionId}
        >
          <p id={confirmationDescriptionId}>
            {confirmation === "live"
              ? "A live run may call tools, change data, or send notifications."
              : confirmation === "test"
                ? "A test uses live reads and models, so it can incur model cost. Mutating tools and notifications are suppressed."
                : archiveDefinition?.status === "published"
                  ? "Archiving stops future triggers but does not cancel work already queued."
                  : "Archiving removes this draft from the working list."}
          </p>
          <div>
            <button
              ref={confirmationActionRef}
              className="btn primary"
              type="button"
              disabled={run.isPending || archive.isPending}
              onClick={() => {
                if (confirmation === "live" && published) {
                  run.mutate({ workflow: published, mode: "live" });
                } else if (confirmation === "test" && testDefinition) {
                  run.mutate({ workflow: testDefinition, mode: "dry_run" });
                } else if (confirmation === "archive" && archiveDefinition) {
                  archive.mutate(archiveDefinition);
                }
              }}
            >
              {run.isPending || archive.isPending
                ? "Working…"
                : confirmation === "live"
                  ? "Start live run"
                  : confirmation === "test"
                    ? "Start test"
                    : "Archive definition"}
            </button>
            <button
              className="btn"
              type="button"
              disabled={run.isPending || archive.isPending}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="agents-automation-actions">
          {canRun && published?.manual_run_supported ? (
            <button
              ref={liveTriggerRef}
              className="btn primary"
              type="button"
              aria-label={`Run ${series.displayName} now`}
              onClick={() => requestConfirmation("live", liveTriggerRef)}
            >
              Run now
            </button>
          ) : null}
          {canRun && testDefinition ? (
            <button
              ref={testTriggerRef}
              className="btn"
              type="button"
              aria-label={`Test ${series.displayName}`}
              onClick={() => requestConfirmation("test", testTriggerRef)}
            >
              Test run
            </button>
          ) : null}
          {canWrite && draft ? (
            <button
              className="btn"
              type="button"
              aria-label={`Edit ${series.displayName} in advanced builder`}
              onClick={() => onEdit(draft.workflow_id)}
            >
              Edit setup
            </button>
          ) : null}
          {canWrite && archiveDefinition ? (
            <button
              ref={archiveTriggerRef}
              className="btn agents-archive"
              type="button"
              aria-label={`Archive ${series.displayName}`}
              onClick={() => requestConfirmation("archive", archiveTriggerRef)}
            >
              {published ? "Archive" : "Archive draft"}
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}

function AutomationSection({
  id,
  title,
  detail,
  series,
  empty,
  onEdit,
  onRan,
}: {
  id: string;
  title: string;
  detail: string;
  series: WorkflowSeries[];
  empty: string;
  onEdit: (workflowId: string) => void;
  onRan: (runId: string) => void;
}) {
  return (
    <Surface className="agents-section" aria-labelledby={id}>
      <SurfaceHeader title={title} titleId={id} detail={detail} count={series.length} />
      {series.length > 0 ? (
        <div className="agents-automation-grid">
          {series.map((workflowSeries) => (
            <AutomationCard
              key={workflowSeries.key}
              series={workflowSeries}
              onEdit={onEdit}
              onRan={onRan}
            />
          ))}
        </div>
      ) : (
        <div className="agents-empty">{empty}</div>
      )}
    </Surface>
  );
}

export default function Agents() {
  const canWrite = useCan("workflows.write");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "runs" ? "runs" : "automations";
  const builder = searchParams.get("builder");
  const selectedRun = searchParams.get("run");
  const statusFilter = searchParams.get("status") ?? "";
  const definitionFilter = searchParams.get("definition") ?? "";
  const workflows = useQuery({ queryKey: ["workflows"], queryFn: () => fetchWorkflows() });
  const editingDetail = useQuery({
    queryKey: ["workflow", builder],
    queryFn: () => fetchWorkflow(builder as string),
    enabled: builder !== null && builder !== "new",
    retry: false,
  });
  const grouped = useMemo(() => groupWorkflows(workflows.data?.workflows ?? []), [workflows.data]);
  const library = grouped.filter((series) => series.isLibrary);
  const custom = grouped.filter((series) => !series.isLibrary);

  const counts = useMemo(() => {
    let armed = 0;
    let off = 0;
    let liveAttention = 0;
    let failedTests = 0;
    for (const series of grouped) {
      if (series.published?.trigger?.enabled === true && series.published.trigger.type !== "manual") {
        armed += 1;
      }
      if (series.published?.trigger?.enabled === false && series.published.trigger.type !== "manual") {
        off += 1;
      }
      const liveStatus = series.published?.last_live_run?.status;
      if (liveStatus === "failed" || liveStatus === "waiting_on_approval") liveAttention += 1;
      const testStatus = (series.draft?.last_test_run ?? series.published?.last_test_run)?.status;
      if (testStatus === "failed") failedTests += 1;
    }
    return { armed, off, liveAttention, failedTests };
  }, [grouped]);

  function setParams(next: Record<string, string | null>) {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (!value) params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params);
  }

  function showRun(runId: string) {
    setParams({ tab: "runs", run: runId, builder: null });
  }

  function handleTabKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const nextTab =
      event.key === "Home"
        ? "automations"
        : event.key === "End"
          ? "runs"
          : event.key === "ArrowLeft"
            ? tab === "automations" ? "runs" : "automations"
            : tab === "runs" ? "automations" : "runs";
    document.getElementById(`agents-${nextTab}-tab`)?.focus();
    setParams({ tab: nextTab === "runs" ? "runs" : null, run: null, builder: null });
  }

  const builderInitial = builder === "new" ? null : editingDetail.data?.workflow ?? null;

  return (
    <div className="agents-page">
      <header className="agents-pagehead">
        <div>
          <div className="agents-eyebrow">Research automation</div>
          <h1>Agents</h1>
          <p>
            Manage recurring and event-driven research jobs. Company thesis conditions remain in{" "}
            <Link to="/monitors">Monitors</Link>; deliberate IC work starts from a company workspace.
          </p>
        </div>
        <div className="agents-page-actions">
          {tab === "automations" ? (
            <button
              className="btn"
              type="button"
              disabled={workflows.isFetching}
              onClick={() => void workflows.refetch()}
            >
              {workflows.isFetching ? "Refreshing…" : "Refresh"}
            </button>
          ) : null}
          {canWrite ? (
            <button
              className="btn primary"
              type="button"
              onClick={() => setParams({ tab: null, run: null, builder: "new" })}
            >
              New automation
            </button>
          ) : null}
        </div>
      </header>

      <div className="agents-tabs" role="tablist" aria-label="Agents views">
        <button
          id="agents-automations-tab"
          role="tab"
          type="button"
          aria-selected={tab === "automations"}
          aria-controls="agents-automations-panel"
          tabIndex={tab === "automations" ? 0 : -1}
          onKeyDown={handleTabKey}
          onClick={() => setParams({ tab: null, run: null, builder: null })}
        >
          Automations
        </button>
        <button
          id="agents-runs-tab"
          role="tab"
          type="button"
          aria-selected={tab === "runs"}
          aria-controls="agents-runs-panel"
          tabIndex={tab === "runs" ? 0 : -1}
          onKeyDown={handleTabKey}
          onClick={() => setParams({ tab: "runs", builder: null })}
        >
          Run history
        </button>
      </div>

      {tab === "automations" ? (
        <div
          id="agents-automations-panel"
          role="tabpanel"
          aria-labelledby="agents-automations-tab"
          tabIndex={0}
        >
          {builder ? (
            <div className="agents-builder-shell">
              <div className="agents-builder-intro">
                <div>
                  <span>Advanced builder</span>
                  <h2>{builder === "new" ? "Create an automation" : "Edit draft automation"}</h2>
                  <p>Configure the trigger and execution steps. Publishing can arm automatic work.</p>
                </div>
                <button className="btn" type="button" onClick={() => setParams({ builder: null })}>
                  Back to automations
                </button>
              </div>
              {builder !== "new" && editingDetail.isLoading ? (
                <div className="agents-state" role="status">Loading the draft…</div>
              ) : builder !== "new" && (editingDetail.isError || !builderInitial) ? (
                <div className="agents-state agents-state--error" role="alert">
                  <strong>The draft could not be loaded.</strong>
                  <button className="btn" type="button" onClick={() => void editingDetail.refetch()}>
                    Try again
                  </button>
                </div>
              ) : (
                <WorkflowBuilder
                  key={builder}
                  workflowId={builder === "new" ? null : builder}
                  initial={builderInitial}
                  onClose={() => setParams({ builder: null })}
                  onSaved={() => {
                    setParams({ builder: null });
                    void workflows.refetch();
                  }}
                />
              )}
            </div>
          ) : workflows.isLoading ? (
            <div className="agents-state" role="status">Loading research automations…</div>
          ) : workflows.isError && !workflows.data ? (
            <div className="agents-state agents-state--error" role="alert">
              <strong>Automations could not be loaded.</strong>
              <span>Check the connection and try again.</span>
              <button className="btn" type="button" onClick={() => void workflows.refetch()}>
                Try again
              </button>
            </div>
          ) : (
            <>
              {workflows.isError ? (
                <div className="agents-refresh-warning" role="alert">
                  Refresh failed. The last loaded automation state is still shown.
                </div>
              ) : null}
              <div className="agents-metrics" aria-label="Automation summary">
                <div><strong>{grouped.length}</strong><span>Automations</span></div>
                <div><strong>{counts.armed}</strong><span>Armed</span></div>
                <div><strong>{counts.off}</strong><span>Off</span></div>
                <div className={counts.liveAttention > 0 ? "is-alert" : ""}>
                  <strong>{counts.liveAttention}</strong><span>Live attention</span>
                </div>
                <div className={counts.failedTests > 0 ? "is-test" : ""}>
                  <strong>{counts.failedTests}</strong><span>Failed tests</span>
                </div>
              </div>
              <div className="agents-sections">
                <AutomationSection
                  id="agents-library-title"
                  title="Built-in automations"
                  detail="Maintained research jobs. Their automatic trigger state is shown exactly as deployed."
                  series={library}
                  empty="No built-in automations are available in this environment."
                  onEdit={(workflowId) => setParams({ builder: workflowId })}
                  onRan={showRun}
                />
                <AutomationSection
                  id="agents-custom-title"
                  title="Custom automations"
                  detail="Drafts and published definitions created for this Athena environment."
                  series={custom}
                  empty={canWrite ? "No custom automations yet. Create one in the advanced builder." : "No custom automations are available."}
                  onEdit={(workflowId) => setParams({ builder: workflowId })}
                  onRan={showRun}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          id="agents-runs-panel"
          className="agents-runs"
          role="tabpanel"
          aria-labelledby="agents-runs-tab"
          tabIndex={0}
        >
          <WorkflowRuns
            definitions={workflows.data?.workflows ?? []}
            statusFilter={statusFilter}
            definitionFilter={definitionFilter}
            onFilterChange={(status, definitionId) =>
              setParams({ status: status || null, definition: definitionId || null, run: null })
            }
            selectedRunId={selectedRun}
            onSelectRun={(runId) => setParams({ run: runId })}
          />
        </div>
      )}
    </div>
  );
}
